import CryptoJS from 'crypto-js';

type SignaturePayload = Record<string, unknown> | string;

type JsonValue = string | number | boolean | null | JsonValue[] | JsonObject;

interface JsonObject {
  [key: string]: JsonValue;
}

const SKIP_KEYS = ['actionplatform', 'attachment', 'attachments', 'file', 'files'];

const isEmptyValue = (value: unknown): boolean => value === null || value === '';

const shouldSkipKey = (key?: string): boolean => !!key && SKIP_KEYS.includes(key.toLowerCase());

export const sortObjectKeys = (obj: JsonValue | Record<string, unknown> | undefined): JsonValue | undefined => {
  // primitives → return as-is
  if (
    obj === null ||
    obj === undefined ||
    typeof obj === 'string' ||
    typeof obj === 'number' ||
    typeof obj === 'boolean'
  ) {
    return obj as JsonValue | undefined;
  }

  // arrays → normalize items, DO NOT reorder
  if (Array.isArray(obj)) {
    return obj.map((item) => sortObjectKeys(item) as JsonValue);
  }

  // objects → canonical key sorting
  if (typeof obj === 'object') {
    const sorted: JsonObject = {};
    Object.keys(obj)
      // Match StringComparer.Ordinal
      .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))
      .forEach((key) => {
        if (shouldSkipKey(key)) return;
        const value = obj[key];
        if (isEmptyValue(value)) return;

        //@ts-expect-error recursive typing
        const sortedValue = sortObjectKeys(value);

        sorted[key] = sortedValue as JsonValue;
      });
    return sorted;
  }

  return obj;
};

const computeHmacSha512Hash = (request: string, secretKey: string): string => {
  const message = CryptoJS.enc.Utf8.parse(request);
  const key = CryptoJS.enc.Utf8.parse(secretKey);

  const hash = CryptoJS.HmacSHA512(message, key);

  return hash.toString(CryptoJS.enc.Hex).toUpperCase();
};

export const generateSignature = (payload: SignaturePayload, secretKey: string): string => {
  if (!secretKey) {
    throw new Error('Secret key is required');
  }

  try {
    // Match Postman raw body behavior
    const requestBodyObject = typeof payload === 'string' ? (JSON.parse(payload) as Record<string, unknown>) : payload;

    if (!requestBodyObject || typeof requestBodyObject !== 'object' || Array.isArray(requestBodyObject)) {
      throw new Error('Invalid request body');
    }

    //Canonical sorting
    const sortedObject = sortObjectKeys(requestBodyObject);

    //Exact serialization
    const serializedBody = JSON.stringify(sortedObject);

    //Generate signature
    return computeHmacSha512Hash(serializedBody, secretKey);
  } catch (error) {
    throw new Error('Error generating signature: ' + (error instanceof Error ? error.message : String(error)));
  }
};
